import axios from "axios";
import { authServiceSignIn, authServiceSignOut, fetchMe } from "app/api/auth.api";
import { setSession, clearSession, setSessionChecked, setLoading, setError } from "./session.slice";

const TOKEN_KEY = "partner_portal_token";
const USER_KEY = "partner_portal_user";
// Auth-service session identifiers come from the signin RESPONSE BODY (not the
// JWT — the shared signin API does not put them in the token). They are only
// needed for the signout call that deletes the server-side session.
const SESSION_ID_KEY = "partner_portal_session_id";
const SESSION_EXP_KEY = "partner_portal_session_expiration";

const persistSession = (token, user) => {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(USER_KEY, JSON.stringify(user));
  axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
};

const clearLocalSession = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem(SESSION_ID_KEY);
  localStorage.removeItem(SESSION_EXP_KEY);
  delete axios.defaults.headers.common["Authorization"];
};

export const checkSession = () => async (dispatch) => {
  const token = localStorage.getItem(TOKEN_KEY);
  const userJson = localStorage.getItem(USER_KEY);

  if (!token || !userJson) {
    dispatch(setSessionChecked());
    return;
  }

  axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  try {
    const { data } = await fetchMe();
    const user = data.data;
    persistSession(token, user);
    dispatch(setSession({ token, user }));
  } catch (e) {
    clearLocalSession();
    dispatch(clearSession());
  }
};

export const signIn = ({ email, password }) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    const signInResponse = await authServiceSignIn({ email, password });
    const authData = signInResponse.data?.data;
    const token = authData?.access_token;
    if (!token) {
      const message = signInResponse.data?.message || "Login failed.";
      dispatch(setError(message));
      return { success: false, message };
    }

    if (authData.session_id && authData.session_expiration) {
      localStorage.setItem(SESSION_ID_KEY, authData.session_id);
      localStorage.setItem(SESSION_EXP_KEY, String(authData.session_expiration));
    }

    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

    const meResponse = await fetchMe();
    const user = meResponse.data.data;

    persistSession(token, user);
    dispatch(setSession({ token, user }));
    return { success: true };
  } catch (error) {
    clearLocalSession();
    const message =
      error.response?.data?.message ||
      "Login failed. Please check your credentials or contact your admin.";
    dispatch(setError(message));
    return { success: false, message };
  }
};

export const logout = () => async (dispatch) => {
  const sessionId = localStorage.getItem(SESSION_ID_KEY);
  const sessionExpiration = localStorage.getItem(SESSION_EXP_KEY);
  if (sessionId && sessionExpiration) {
    // Delete the server-side session first (bounded by the request timeout);
    // local cleanup happens regardless of the outcome. The token itself stays
    // valid until its exp — same logout semantics as the other portals.
    try {
      await authServiceSignOut({ sessionId, sessionExpiration });
    } catch (e) { /* proceed with local cleanup */ }
  }
  clearLocalSession();
  dispatch(clearSession());
};
